import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button, buttonVariants } from '@/components/ui/button'
import { Globe } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import useCourses from '@/hooks/courses/useCourses'
import { getCoursesNamesAndSections } from '@/utils/courseUtils'
import {
  MultiSelector,
  MultiSelectorContent,
  MultiSelectorInput,
  MultiSelectorItem,
  MultiSelectorList,
  MultiSelectorTrigger,
} from '@/components/ui/multi-select'
import { useWatch } from 'react-hook-form'
import { useAtomValue } from 'jotai'
import { userAtom } from '@/store/store'
import useAddDocumentFromURL from '@/hooks/documents/useAddDocumentFromURL'

const formSchema = z.object({
  name: z.string().min(1, { message: 'Please enter a document display name.' }),
  url: z.string().url({ message: 'Please enter a valid URL.' }),
  course: z.string({ required_error: 'Course is required' }),
  sections: z.array(z.string()).nonempty('Select at least one section'),
})

type FormValues = z.infer<typeof formSchema>

export default function NewDocumentFromUrlDialog() {
  const [isOpen, setIsOpen] = useState(false)
  const user = useAtomValue(userAtom)
  const { mutate, isPending } = useAddDocumentFromURL()

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      url: '',
      course: '',
      sections: [],
    },
  })

  let coursesNamesAndSections: { courseName: string; sections: string[] }[] = []
  const selectedCourse: string | undefined = useWatch({
    control: form.control,
    name: 'course',
    defaultValue: '',
  })

  let selectedCourseSections: string[] | undefined = []

  const { data: coursesList, isSuccess: isCourseSuccess, isError: isCourseError } = useCourses()

  if (isCourseSuccess && coursesList) {
    coursesNamesAndSections = getCoursesNamesAndSections(coursesList)
    selectedCourseSections = selectedCourse
      ? coursesNamesAndSections?.find((course) => course.courseName === selectedCourse)?.sections
      : []
  } else if (isCourseError) {
    toast.error('An error occurred while loading the courses data. Please try again later or refresh the page.')
  }

  const onSubmit = async (data: FormValues) => {
    try {
      if (!user?.userId) return

      console.log(data)

      mutate(
        {
          url: data.url,
          courseName: data.course,
          fileName: data.name + '.html',
          createdBy: user.userId,
          sections: data.sections,
        },
        {
          onSuccess: () => {
            form.reset()
            setIsOpen(false)
          },
        },
      )
    } catch (error) {
      if (error instanceof Error) {
        console.error('An error occurred while submitting the form:', error)
        toast.error(`Error adding document: ${error.message}`)
      } else {
        toast.error('Error adding document')
      }
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger className={`${buttonVariants({ size: 'sm' })} ml-auto h-8 lg:flex`}>
        <Globe className="mr-2 h-4 w-4" />
        Add Web Page
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="pb-4">Add New Document from URL</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Display Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Display Name" {...field} />
                    </FormControl>
                    <FormDescription className="text-xs">
                      A recognizable nickname for this web page that will be displayed in the system
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>URL</FormLabel>
                    <FormControl>
                      <Input placeholder="https://example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col gap-3">
              <FormField
                control={form.control}
                name="course"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Course</FormLabel>
                    <Select
                      name="select-course"
                      onValueChange={(value) => {
                        field.onChange(value)
                        form.resetField('sections')
                      }}
                      defaultValue={field.value}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select Course" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent side="bottom">
                        {coursesNamesAndSections.map((course) => (
                          <SelectItem value={course.courseName} key={course.courseName}>
                            {course.courseName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="sections"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Select Sections</FormLabel>
                    <MultiSelector onValuesChange={field.onChange} values={field.value}>
                      <MultiSelectorTrigger>
                        <MultiSelectorInput
                          className={`${!selectedCourse ? 'cursor-not-allowed' : ''}`}
                          disabled={!selectedCourse}
                          placeholder="Select sections"
                        />
                      </MultiSelectorTrigger>
                      <MultiSelectorContent>
                        <MultiSelectorList>
                          {selectedCourseSections?.map((section) => (
                            <MultiSelectorItem key={section} value={section}>
                              <div className="flex items-center space-x-2">
                                <span>{section}</span>
                              </div>
                            </MultiSelectorItem>
                          ))}
                        </MultiSelectorList>
                      </MultiSelectorContent>
                    </MultiSelector>
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Adding...' : 'Add Web Page'}
              </Button>
            </div>
          </form>
        </Form>
        <DialogDescription className="sr-only">
          Form to add a new document from a URL. Enter display name, URL, select course and sections.
        </DialogDescription>
      </DialogContent>
    </Dialog>
  )
}
